// FLAG:useDocuAi — moving a reviewed document between the two LEDGERS (Ausgabe ↔ Einnahme) and what
// that does to the payments the reviewer has entered but not saved yet.
//
// Deliberately its own module rather than part of save-document-invoice-review.ts: receipt-edit.tsx
// imports that file on a single line which review-payee-seam.spec.ts pins verbatim, and a second
// import of the same path is an `import/no-duplicates` error.
import type { ReviewBooking } from '@/modules/receipt/review/save-document-invoice-review'

const LEDGER_TARGETS = ['expense', 'revenue']

// Does this target change move the document between the two ledgers? „Nur Dokument" is not a ledger,
// so switching to it changes no sign; coming back from it is judged against the last ledger the
// reviewer had chosen (the caller remembers that).
export const isReviewLedgerFlip = (previousTarget: unknown, nextTarget: unknown) => {
  return LEDGER_TARGETS.includes(previousTarget as string)
    && LEDGER_TARGETS.includes(nextTarget as string)
    && previousTarget !== nextTarget
}

const flipAmount = (amount: number) => (amount === 0 ? 0 : -amount)

// An amount is always stated FROM THE RECEIPT's point of view: the same 1071 € payment is booked as
// -1071 on an expense and as +1071 on a revenue. So when the reviewer moves the document to the
// other ledger, a payment they entered but have not saved yet has to move with it — otherwise the
// review saves a payment whose sign contradicts the chosen ledger.
//
// Only `isNew` bookings: anything already persisted belongs to a receipt that the ledger switch does
// not touch. `bookingAmount` is deliberately NOT flipped either — that is the real bank
// transaction's own amount, a fact that does not change with the ledger; only the share assigned to
// this receipt does.
export const flipReviewBookingAmounts = (bookings: ReviewBooking[] = []) => (bookings ?? []).map(booking => {
  if (!booking?.isNew) { return booking }

  const flipped: ReviewBooking = { ...booking }
  if (typeof booking.amount === 'number') {
    flipped.amount = flipAmount(booking.amount)
  }
  if (Array.isArray(booking.assignments)) {
    flipped.assignments = booking.assignments.map(assignment => (
      typeof assignment?.amount === 'number'
        ? { ...assignment, amount: flipAmount(assignment.amount) }
        : assignment
    ))
  }
  return flipped
})
